import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useMemo, useState } from "react";
import { RemoveAccessDialog } from "@/components/sharing/remove/RemoveAccessDialog";
import { useRemoveWithUndo } from "@/components/sharing/remove/useRemoveWithUndo";
import { useQueryClient } from "@tanstack/react-query";
import { testIds } from "@/testIds";
import { useSharingApi } from "@/hooks/useSharingApi";
import type { UserCollaborator } from "@/lib/api";
import { PublicLinkSection } from "@/components/sharing/sections/PublicLinkSection";
import { canonicalizeRole, roleLabel } from "@/lib/sharing/roleLabels";
import { useNewArrivals, usePendingInvites } from "@/components/sharing/pending/usePendingInvites";
import { inviteDateLabel, peopleSummary } from "@/lib/sharing/pendingInvites";
import styles from "./MobileReader.module.scss";

const M = testIds.mobile;

export interface ShareState {
  isPublic: boolean;
  shareLink: string;
  shareSlug: string;
  linkPermission: string;
  linkExpiresAt: string | null;
}

export interface MobileShareSheetProps {
  documentId: string;
  /** The owner manages sharing; anyone else sees Copy link and the people list. */
  canManage: boolean;
  ownerId?: string;
  /** Listed first, as Owner, in the read-only people list. */
  ownerName?: string;
  initial: ShareState;
  onCopied: () => void;
}

/**
 * Share on a phone (Olivia, 10 Oct, ⋯ → Share…): Copy link at the top, the
 * public link switch (the desktop drawer's own), and the people with access,
 * owner first. The owner changes a role or removes someone with the phone's own
 * picker, and invites by email: the one place sharing needs the keyboard (the
 * user, 10 Oct: "activate sharing in mobile also", after a no-keyboard pass had
 * sent inviting to desktop). Anyone else sees the list, read-only.
 */
export function MobileShareSheet({ documentId, canManage, ownerId, ownerName, initial, onCopied }: MobileShareSheetProps) {
  const { t } = useTranslation("mobile");
  const sharingApi = useSharingApi();
  const queryClient = useQueryClient();
  const [people, setPeople] = useState<UserCollaborator[]>([]);
  const [loading, setLoading] = useState(true);
  const [share, setShare] = useState<ShareState>(initial);

  // "Loading…" only the first time: a refresh after a change keeps the list in place.
  const load = useCallback(async () => {
    try {
      setPeople((await sharingApi.listCollaborators(documentId)).users);
    } catch {
      setPeople([]);
    } finally {
      setLoading(false);
    }
  }, [documentId, sharingApi]);
  useEffect(() => {
    void load();
  }, [load]);

  const [error, setError] = useState<string | null>(null);
  // Invitations not yet accepted, after the people with access (Olivia, 10 Oct, §c).
  const pending = usePendingInvites(documentId, canManage, () => void load(), setError);
  const arrived = useNewArrivals(useMemo(() => people.map((p) => p.userId), [people]), !loading);
  const [email, setEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<"viewer" | "editor">("viewer");
  const [inviting, setInviting] = useState(false);
  const [invited, setInvited] = useState<string | null>(null);
  const invite = async () => {
    const to = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(to)) {
      setError(t("share.badEmail"));
      return;
    }
    setError(null);
    setInvited(null);
    setInviting(true);
    try {
      const r = await sharingApi.invite(documentId, { email: to, role: inviteRole });
      if (!r.success) {
        setError(r.message || t("share.notSent"));
        return;
      }
      setEmail("");
      setInvited(r.userFound ? t("share.canOpenNow", { email: to }) : t("share.inviteSent", { email: to }));
      await Promise.all([load(), pending.refresh()]);
    } catch {
      setError(t("share.notSentConnection"));
    } finally {
      setInviting(false);
    }
  };

  const [removing, setRemoving] = useState<{ userId: string; name: string; role: string } | null>(null);
  const removeWithUndo = useRemoveWithUndo(documentId, () => void load());
  const changeAccess = async (userId: string, value: string) => {
    setError(null);
    if (value === "__remove") {
      // Ask first (Olivia, 10 Oct): no notice unless the owner ticks it.
      const p = people.find((x) => x.userId === userId);
      setRemoving({ userId, name: p?.name || p?.email || t("share.thisPerson"), role: canonicalizeRole(p?.roleName) === "viewer" ? "viewer" : "editor" });
      return;
    }
    try {
      await sharingApi.updateRole(documentId, userId, value);
      await load();
    } catch {
      setError(t("share.saveFailed"));
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
    } catch {
      // Nothing to undo: the button stays.
    }
    onCopied();
  };

  return (
    <div className={styles.shareBody} data-testid={M.shareSheet}>
      <button type="button" className={styles.primary} data-testid={M.shareCopyLink} onClick={() => void copy()}>
        {t("share.copyLink")}
      </button>
      {canManage && (
        <PublicLinkSection
          documentId={documentId}
          isPublic={share.isPublic}
          shareLink={share.shareLink}
          shareSlug={share.shareSlug}
          linkPermission={share.linkPermission}
          linkExpiresAt={share.linkExpiresAt}
          onChange={(s) => {
            queryClient.setQueryData(["document", documentId], (d: object | undefined) =>
              d && { ...d, isPublic: s.isPublic, shareLink: s.shareLink, shareSlug: s.shareSlug ?? "" },
            );
            setShare((cur) => ({
              ...cur,
              isPublic: s.isPublic,
              shareLink: s.shareLink,
              shareSlug: s.shareSlug ?? "",
              ...(s.linkPermission ? { linkPermission: s.linkPermission } : {}),
              ...(s.linkExpiresAt !== undefined ? { linkExpiresAt: s.linkExpiresAt } : {}),
            }));
          }}
        />
      )}
      {canManage && (
        <form
          className={styles.inviteForm}
          data-testid={M.shareInvite}
          // Lilia says what's wrong, not the browser's bubble.
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            void invite();
          }}
        >
          <h4>{t("share.invitePeople")}</h4>
          <input
            type="email"
            inputMode="email"
            autoComplete="email"
            autoCapitalize="none"
            enterKeyHint="send"
            placeholder={t("share.emailPlaceholder")}
            aria-label={t("share.email")}
            data-testid={M.shareInviteEmail}
            className={styles.titleInput}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <div className={styles.inviteRow}>
            <select
              className={styles.roleSelect}
              aria-label={t("share.access")}
              data-testid={M.shareInviteRole}
              value={inviteRole}
              onChange={(e) => setInviteRole(e.target.value as "viewer" | "editor")}
            >
              <option value="viewer">{t("share.canView")}</option>
              <option value="editor">{t("share.canEdit")}</option>
            </select>
            <button type="submit" className={styles.primary} data-testid={M.shareInviteSend} disabled={inviting || !email.trim()}>
              {inviting ? t("share.inviting") : t("share.invite")}
            </button>
          </div>
          {invited && <p className={styles.muted} role="status">{invited}</p>}
        </form>
      )}
      <section className={styles.peopleList} data-testid={M.sharePeople}>
        <h4>{t("share.peopleWithAccess")}</h4>
        {canManage && !loading && (
          <p className={styles.muted} data-testid={testIds.sharing.peopleSummary}>
            {peopleSummary(people.filter((p) => p.userId !== ownerId).length + 1, pending.invites)}
          </p>
        )}
        {loading ? (
          <p className={styles.muted}>{t("share.loading")}</p>
        ) : (
          <ul>
            <li>
              <span className={styles.personName}>{canManage ? t("share.you") : ownerName || t("share.theOwner")}</span>
              <span className={styles.muted}>{t("share.owner")}</span>
            </li>
            {people
              .filter((p) => p.userId !== ownerId)
              .map((p) => (
                <li key={p.id} data-flash={arrived.has(p.userId) ? "true" : undefined}>
                  <span className={styles.personName}>{p.name || p.email || t("share.someone")}</span>
                  {canManage ? (
                    <select
                      className={styles.roleSelect}
                      data-testid={M.shareRole}
                      data-user-id={p.userId}
                      aria-label={t("share.accessFor", { name: p.name || p.email || t("share.thisPerson") })}
                      value={canonicalizeRole(p.roleName) === "viewer" ? "viewer" : "editor"}
                      onChange={(e) => void changeAccess(p.userId, e.target.value)}
                    >
                      <option value="editor">{t("share.canEdit")}</option>
                      <option value="viewer">{t("share.canView")}</option>
                      <option value="__remove">{t("share.removeAccess")}</option>
                    </select>
                  ) : (
                    <span className={styles.muted}>{roleWord(p.roleName)}</span>
                  )}
                </li>
              ))}
            {pending.invites.map((inv) => (
              <li
                key={inv.id}
                className={styles.pendingRow}
                data-testid={testIds.sharing.pendingInvite}
                data-invite-id={inv.id}
                data-status={inv.status}
              >
                <div className={styles.pendingWho}>
                  <span aria-hidden="true" className={styles.pendingRing}>
                    {inv.email.charAt(0).toUpperCase()}
                  </span>
                  <span className={styles.pendingText}>
                    <span className={styles.personName}>{inv.email}</span>
                    <span className={inv.status === "expired" ? styles.pendingExpired : styles.muted}>
                      {roleLabel(inv.role)} · {inviteDateLabel(inv)}
                    </span>
                  </span>
                </div>
                <div className={styles.pendingActions}>
                  <button type="button" data-testid={testIds.sharing.pendingResend} onClick={() => void pending.resend(inv)}>
                    {t("share.resend")}
                  </button>
                  {inv.status !== "expired" && (
                    <button type="button" data-testid={testIds.sharing.pendingCopyLink} onClick={() => void pending.copyLink(inv)}>
                      {t("share.copyInviteLink")}
                    </button>
                  )}
                  <button
                    type="button"
                    data-testid={testIds.sharing.pendingCancel}
                    aria-label={t("share.cancelInvite", { email: inv.email })}
                    onClick={() => void pending.cancel(inv)}
                  >
                    {t("share.cancel")}
                  </button>
                </div>
                {pending.notes[inv.id] && (
                  <p role="status" className={styles.muted}>
                    {pending.notes[inv.id]}
                  </p>
                )}
              </li>
            ))}
          </ul>
        )}
        {error && <p className={styles.shareError} role="alert">{error}</p>}
        {!canManage && <p className={styles.muted}>{t("share.onlyOwner")}</p>}
      </section>
      <RemoveAccessDialog
        open={!!removing}
        name={removing?.name ?? ""}
        onCancel={() => setRemoving(null)}
        onConfirm={(choice) => {
          const person = removing;
          setRemoving(null);
          if (person) void removeWithUndo(person, choice).catch(() => setError(t("share.saveFailed")));
        }}
      />
    </div>
  );
}

function roleWord(role: string): string {
  const r = role.toLowerCase();
  if (r.includes("owner")) return "Owner";
  if (r.includes("edit")) return "Can edit";
  if (r.includes("comment")) return "Can comment";
  return "Can view";
}
